import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';

import { APP_ROUTES } from './app.constants';
import { AuthComponent } from './components/auth/auth.component';
import { MyCompanyComponent } from './components/company/company.component';
import { DeclineInviteComponent } from './components/decline-invite/decline-invite.component';
import { ErrorComponent } from './components/error/error.component';
import { ForgotPasswordComponent } from './components/forgot-password/forgot-password.component';
import { ProfileComponent } from './components/profile/profile.component';
import { ChangesCarrierInviteComponent } from './components/register-carrier-changes/changes-carrier-invite.component';
import { RegisterCarrierFinishComponent } from './components/register-carrier-finish/register-carrier-finish.component';
import { RegisterCarrierInviteComponent } from './components/register-carrier-invite/register-carrier-invite.component';
import { RegisterCommonComponent } from './components/register-common/register-common.component';
import { ResetPasswordComponent } from './components/reset-password/reset-password.component';
import { WaitingComponent } from './components/waiting/waiting.component';
import { ROLES_ENUM } from './enums';
import { UserService } from './providers/user/user.service';
import { TermsComponent } from './components/terms/terms.component';
import { RegisterCarrierFinalStepComponent } from './components/register-carrier-final-step/register-carrier-final-step.component';
import { RegisterComponent } from './components/register/register.component';

const routes: Routes = [
  { path: '', redirectTo: `/${APP_ROUTES.dashboard}`, pathMatch: 'full' },
  { path: APP_ROUTES.auth, component: AuthComponent },
  { path: APP_ROUTES.register, component: RegisterComponent },
  { path: APP_ROUTES.forgot_password, component: ForgotPasswordComponent },
  { path: APP_ROUTES.terms, component: TermsComponent },
  {
    path: `${APP_ROUTES.register_carrier_finish}/:hash`,
    component: RegisterCarrierFinishComponent,
  },
  {
    path: `${APP_ROUTES.register_carrier_final_step}`,
    component: RegisterCarrierFinalStepComponent,
  },
  {
    path: `${APP_ROUTES.reset_password}/:hash`,
    component: ResetPasswordComponent,
  },
  {
    path: `${APP_ROUTES.reset_password}`,
    component: ResetPasswordComponent,
    canActivate: [UserService],
    data: { roles: [ROLES_ENUM.SUPER_ADMIN, ROLES_ENUM.COMPANY_ADMIN, ROLES_ENUM.DISPATCHER] },
  },
  {
    path: `${APP_ROUTES.register_common}/:hash`,
    component: RegisterCommonComponent,
  },
  {
    path: `${APP_ROUTES.register_carrier}/:hash`,
    component: RegisterCarrierInviteComponent,
  },
  {
    path: `${APP_ROUTES.changes_carrier}/:hash`,
    component: ChangesCarrierInviteComponent,
  },
  {
    path: `${APP_ROUTES.common_decline}/:hash`,
    component: DeclineInviteComponent,
  },
  {
    path: `${APP_ROUTES.carrier_decline}/:hash`,
    component: DeclineInviteComponent,
  },
  { path: `${APP_ROUTES.waiting}/:messageId`, component: WaitingComponent },
  {
    path: `${APP_ROUTES.profile}`,
    component: ProfileComponent,
    canActivate: [UserService],
    data: { roles: [ROLES_ENUM.SUPER_ADMIN] },
  },
  { path: APP_ROUTES.error, component: ErrorComponent },
  {
    path: APP_ROUTES.my_company,
    component: MyCompanyComponent,
    canActivate: [UserService],
    data: { roles: [ROLES_ENUM.COMPANY_ADMIN, ROLES_ENUM.DISPATCHER] },
  },
  {
    path: APP_ROUTES.drivers_location,
    loadChildren: () => import('./drivers-location/drivers-location.module').then((m) => m.DriversLocationModule),
    canActivate: [UserService],
    data: { roles: [ROLES_ENUM.COMPANY_ADMIN, ROLES_ENUM.DISPATCHER, ROLES_ENUM.SUPER_ADMIN] },
  },
  {
    path: `${APP_ROUTES.drivers_location}/:driverId`,
    loadChildren: () => import('./drivers-location/drivers-location.module').then((m) => m.DriversLocationModule),
    canActivate: [UserService],
    data: { roles: [ROLES_ENUM.COMPANY_ADMIN, ROLES_ENUM.SUPER_ADMIN] },
  },
  {
    path: APP_ROUTES.accounts,
    loadChildren: () => import('./accounts/accounts.module').then((m) => m.AccountsModule),
    canActivate: [UserService],
    data: { roles: [ROLES_ENUM.SUPER_ADMIN, ROLES_ENUM.COMPANY_ADMIN, ROLES_ENUM.DISPATCHER] },
  },
  {
    path: APP_ROUTES.company,
    loadChildren: () => import('./companies/companies.module').then((m) => m.CompaniesModule),
    canActivate: [UserService],
    data: { roles: [ROLES_ENUM.SUPER_ADMIN] },
  },
  {
    path: APP_ROUTES.order,
    loadChildren: () => import('./order/order.module').then((m) => m.OrderModule),
    canActivate: [UserService],
    data: { roles: [ROLES_ENUM.COMPANY_ADMIN, ROLES_ENUM.DISPATCHER] },
  },
  {
    path: APP_ROUTES.orders,
    loadChildren: () => import('./orders/orders.module').then((m) => m.OrdersModule),
    canActivate: [UserService],
    data: { roles: [ROLES_ENUM.COMPANY_ADMIN, ROLES_ENUM.DISPATCHER] },
  },
  {
    path: APP_ROUTES.new_order,
    loadChildren: () => import('./new-order/new-order.module').then((m) => m.NewOrderModule),
    canActivate: [UserService],
    data: { roles: [ROLES_ENUM.COMPANY_ADMIN, ROLES_ENUM.DISPATCHER] },
  },
  {
    path: APP_ROUTES.dashboard,
    loadChildren: () => import('./modules/dashboard/dashboard.module').then((m) => m.DashboardModule),
    canActivate: [UserService],
    data: { roles: [ROLES_ENUM.COMPANY_ADMIN, ROLES_ENUM.DISPATCHER] },
  },
  {
    path: APP_ROUTES.sys_order,
    loadChildren: () => import('./sys-orders/sys-orders.module').then((m) => m.SysOrdersModule),
    canActivate: [UserService],
    data: { roles: [ROLES_ENUM.SUPER_ADMIN] },
  },
  {
    path: `${APP_ROUTES.order}/:id`,
    loadChildren: () => import('./order-details/order-details.module').then((m) => m.OrderDetailsModule),
    canActivate: [UserService],
    data: { roles: [ROLES_ENUM.SUPER_ADMIN, ROLES_ENUM.COMPANY_ADMIN, ROLES_ENUM.DISPATCHER] },
  },
  {
    path: APP_ROUTES.trip,
    loadChildren: () => import('./trip/trip.module').then((m) => m.TripModule),
    canActivate: [UserService],
    data: { roles: [ROLES_ENUM.COMPANY_ADMIN, ROLES_ENUM.DISPATCHER] },
  },
  {
    path: `${APP_ROUTES.dispatch}/:id`,
    loadChildren: () => import('./dispatch/dispatch.module').then((m) => m.DispatchModule),
    canActivate: [UserService],
    data: { roles: [ROLES_ENUM.SUPER_ADMIN] },
  },
  {
    path: APP_ROUTES.leeds,
    loadChildren: () => import('./leads/lead.module').then((m) => m.LeadModule),
    canActivate: [UserService],
    data: { roles: [ROLES_ENUM.SUPER_ADMIN, ROLES_ENUM.COMPANY_ADMIN, ROLES_ENUM.DISPATCHER] },
  },
  {
    path: `${APP_ROUTES.quote}/:id`,
    loadChildren: () => import('./quote-details/quote-details.module').then((m) => m.QuoteDetailsModule),
    canActivate: [UserService],
    data: { roles: [ROLES_ENUM.SUPER_ADMIN] },
  },
  {
    path: APP_ROUTES.policy,
    loadChildren: () => import('./policy/policy.module').then((m) => m.PolicyModule),
    canActivate: [UserService],
    data: { roles: [ROLES_ENUM.SUPER_ADMIN] },
  },
  {
    path: APP_ROUTES.reports,
    loadChildren: () => import('./reports/reports.module').then((m) => m.ReportsModule),
    canActivate: [UserService],
    data: { roles: [ROLES_ENUM.COMPANY_ADMIN, ROLES_ENUM.DISPATCHER] },
  },
  { path: '*', redirectTo: `/${APP_ROUTES.dashboard}`, pathMatch: 'full' },
  { path: '**', redirectTo: `/${APP_ROUTES.dashboard}`, pathMatch: 'full' },
];

@NgModule({
  imports: [RouterModule.forRoot(routes)],
  exports: [RouterModule],
})
export class AppRoutingModule { }
